import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { FormGroup, Validators, FormControl, ReactiveFormsModule, NonNullableFormBuilder } from '@angular/forms';
import { PasswordService } from '@app/core/services/password.service';
import { AccountService } from '@api';
import { WaitIndicatorService } from '@app/core/services/wait-indicator.service';
import { FormsService } from '@app/core/services/forms.service';
import { shareReplay, map, catchError, take, tap, finalize } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';
import { AlertService } from '@app/core/services/alert.service';
import { ExternalNavService } from '@app/core/services/external-nav.service';
import { WtAutofocusDirective } from '@app/core/directives/wt-autofocus.directive';
import { WtFocusableDirective } from '@app/core/directives/wt-focusable.directive';
import { WtValidateOnBlurDirective } from '@app/core/directives/wt-validate-on-blur.directive';
import { WtValidationStateDirective } from '@app/core/directives/wt-validation-state.directive';
import { AsyncPipe } from '@angular/common';
import { WtValidationMessagesComponent, WtValidationMessageComponent } from '@app/core/components/wt-validation-errors.component';
import { ConfigService } from '@app/core/services/config.service';

function formValidation(group: FormGroup) {
  const errors = {};

  if (group.controls['password'].value !== group.controls['password2'].value) {
    group.controls['password2'].setErrors({
      mismatch: true,
    });
  }

  return errors;
}

@Component({
  selector: 'app-reset-password',
  template: ` <h2 class="title">Reset Password</h2>
    @switch (validNonce()) {
      @case (null) {
        Please wait...
      }
      @case (false) {
        <div class="alert alert-danger" role="alert">
          <span class="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
          <span class="sr-only">Error:</span>

          Your reset password request has expired.
        </div>

        <a class="btn btn-primary" href="/loanmanager/request_password_reset">Request password reset</a>
      }
      @case (true) {
        <p>Please fill out the following form to reset your password:</p>

        <form [formGroup]="form" class="form-horizontal col-sm-8" (ngSubmit)="onSubmit()" novalidate>
          <div class="form-group required" [wtValidationState]="validator.getControlStatus('password') | async">
            <label class="col-md-3 control-label" for="id_password"> Password </label>
            <div class="col-md-9">
              <input
                formControlName="password"
                class="form-control"
                data-testid="password-1"
                id="id_password"
                maxlength="50"
                name="password"
                placeholder="Enter your password"
                required="required"
                type="password"
                wtValidateOnBlur
                wtFocusable
                [wtAutofocus]="true"
              />
              <p class="help-block">{{ passwordMessage }}</p>
              <wt-validation-messages [validator]="validator" controlName="password">
                <wt-validation-message name="required">This field is required</wt-validation-message>
                <wt-validation-message name="password">Password requirements not met.</wt-validation-message>
                <wt-validation-message name="maxLength">Maximum length is 200.</wt-validation-message>
              </wt-validation-messages>
            </div>
          </div>

          <div class="form-group required" [wtValidationState]="validator.getControlStatus('password2') | async">
            <label class="col-md-3 control-label" for="id_password2"> Password (again) </label>
            <div class="col-md-9">
              <input
                formControlName="password2"
                class="form-control"
                data-testid="password-2"
                id="id_password2"
                maxlength="50"
                name="password2"
                placeholder="Please re-enter your password for verification"
                required="required"
                type="password"
                wtValidateOnBlur
                wtFocusable
              />
              <wt-validation-messages controlName="password2" [validator]="validator">
                <wt-validation-message name="mismatch">The passwords you entered do not match</wt-validation-message>
              </wt-validation-messages>
            </div>
          </div>
          <div class="form-group">
            <div class="col-md-offset-3 col-md-9">
              <button class="btn btn-primary" type="submit">Reset Password</button>
            </div>
          </div>
        </form>
      }
    }`,
  imports: [
    ReactiveFormsModule,
    WtValidationStateDirective,
    WtValidateOnBlurDirective,
    WtFocusableDirective,
    WtAutofocusDirective,
    WtValidationMessagesComponent,
    WtValidationMessageComponent,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private passwordService = inject(PasswordService);
  private accountService = inject(AccountService);
  private waitIndicatorService = inject(WaitIndicatorService);
  private formsService = inject(FormsService);
  private configService = inject(ConfigService);
  private alertService = inject(AlertService);
  private externalNav = inject(ExternalNavService);

  form = this.fb.group(
    {
      password: ['', [Validators.required, (x: FormControl) => this.passwordService.checkPasswordValidator(x), Validators.maxLength(200)]],
      password2: ['', [Validators.required, Validators.maxLength(200)]],
    },
    { validators: [formValidation] }
  );
  validator = this.formsService.getFormValidator(this.form);
  passwordMessage = this.passwordService.getMessage();
  validNonce = signal<boolean | null>(null);

  ngOnInit() {
    this.waitIndicatorService.setWaiting();

    this.accountService
      .accountValidateNonceCreate({
        validateNonce: {
          nonce: this.configService.getConfig().nonce ?? '',
        },
      })
      .pipe(
        tap(() => {
          this.validNonce.set(true);
        }),
        catchError((error) => {
          console.error(error);
          this.validNonce.set(false);
          return of(false);
        }),
        finalize(() => this.waitIndicatorService.setReady()),
        take(1)
      )
      .subscribe();
  }

  async onSubmit() {
    this.validator.setSubmitted();

    if (!this.form.valid) {
      return;
    }

    this.waitIndicatorService.setWaiting();

    await firstValueFrom(
      this.accountService
        .accountResetPasswordCreate({
          resetPassword: {
            nonce: this.configService.getConfig().nonce ?? '',
            password: this.form.getRawValue().password,
            password2: this.form.getRawValue().password2,
          },
        })
        .pipe(
          map(() => {
            this.alertService.alert('Password reset successful! You will now be redirected to the login page.');
            this.externalNav.go('/loanmanager/login/');
          }),
          catchError((error) => {
            this.alertService.alert('There was a problem resetting your password');
            return of(error);
          })
        )
        .pipe(
          map(() => {
            this.waitIndicatorService.setReady();
          }),
          shareReplay()
        )
        .pipe(take(1))
    );
  }
}
